/**
 * C.13a — Dataview runner. Pure. Never throws.
 *
 * Inputs: a DataviewAst + a ParsedNote[] (from `loadSampleVault()` or a
 * Convex query). Output: a `RunnerOutcome` (`{ ok: true, rows: ... }`
 * or `{ ok: false, message }`).
 *
 * No chevrotain dependency: this module ships eagerly so the runner can
 * be reused by Bases (C.13b) without paying the parser cost.
 */

import type { ParsedNote } from '../parse';
import type {
  Column,
  ComparisonOp,
  DataviewAst,
  FromAtom,
  FromClause,
  LiteralValue,
  RunnerCell,
  RunnerOutcome,
  RunnerRow,
  WhereExpr,
} from './types';

export interface RunnerOptions {
  /**
   * Cap the number of rows returned. SORT/LIMIT are FT-3, but we still
   * need a hard ceiling so a runaway query can't lock the editor.
   */
  hardLimit?: number;
}

const DEFAULT_HARD_LIMIT = 500;
const DEFAULT_COLUMNS: Column[] = [{ field: 'file.link' }];

export function executeQuery(
  ast: DataviewAst,
  notes: ReadonlyArray<ParsedNote>,
  options: RunnerOptions = {},
): RunnerOutcome {
  const hardLimit = options.hardLimit ?? DEFAULT_HARD_LIMIT;
  const columns =
    ast.type === 'LIST'
      ? DEFAULT_COLUMNS
      : ast.columns.length === 0
        ? DEFAULT_COLUMNS
        : ast.columns;

  // FROM filtering.
  const filtered = notes.filter((note) => matchesFrom(note, ast.from));

  // WHERE filtering. Errors here ride through as `false` per the
  // "never throws" contract — undefined-field comparisons just fail.
  const matched = ast.where
    ? filtered.filter((note) => evaluateWhere(ast.where!, note))
    : filtered;

  // Stable order by file.name (grammar excludes SORT — FT-3). Empty
  // names sort first to keep the result deterministic.
  const sorted = [...matched].sort((a, b) => {
    const an = a.name ?? '';
    const bn = b.name ?? '';
    return an.localeCompare(bn);
  });

  const limited = sorted.slice(0, hardLimit);

  const rows: RunnerRow[] = limited.map((note) => ({
    path: note.path,
    cells: Object.fromEntries(
      columns.map((c) => [c.field, projectField(note, c.field)]),
    ),
  }));

  return { ok: true, type: ast.type, columns, rows };
}

// ============================================================================
// FROM matching
// ============================================================================

function matchesFrom(note: ParsedNote, from: FromClause): boolean {
  if (from.atoms.length === 0) return true; // FROM-less query = all notes
  return from.atoms.every((atom) => matchesFromAtom(note, atom));
}

function matchesFromAtom(note: ParsedNote, atom: FromAtom): boolean {
  if (atom.kind === 'tag') {
    // tag includes the leading "#" per the grammar normalization.
    const needle = atom.tag.startsWith('#') ? atom.tag.slice(1) : atom.tag;
    return note.tags.some((t) => t === needle);
  }
  // folder: recursive prefix match. "Cases" matches "Cases/Hadley.md".
  const folder = atom.folder.replace(/\/$/, '');
  if (folder === '') return true;
  const prefix = folder + '/';
  return note.path === folder || note.path.startsWith(prefix);
}

// ============================================================================
// WHERE evaluation
// ============================================================================

function evaluateWhere(expr: WhereExpr, note: ParsedNote): boolean {
  switch (expr.kind) {
    case 'and':
      return evaluateWhere(expr.left, note) && evaluateWhere(expr.right, note);
    case 'or':
      return evaluateWhere(expr.left, note) || evaluateWhere(expr.right, note);
    case 'not':
      return !evaluateWhere(expr.inner, note);
    case 'includes':
      // grammar restricts to file.tags
      return note.tags.includes(stripHash(expr.value));
    case 'comparison':
      return evaluateComparison(expr.field, expr.op, expr.value, note);
  }
}

function evaluateComparison(
  field: string,
  op: ComparisonOp,
  literal: LiteralValue,
  note: ParsedNote,
): boolean {
  const left = resolveFieldRaw(note, field);
  const right = literalValue(literal);

  if (left === undefined || left === null) {
    // Undefined LHS — only `!=` against a non-null literal returns true.
    if (op === '!=' && right !== null && right !== undefined) return true;
    if (op === '==' && (right === null || right === undefined)) return true;
    return false;
  }

  if (op === 'contains') {
    if (typeof left !== 'string' || typeof right !== 'string') return false;
    return left.includes(right);
  }

  if (op === '==') return looseEq(left, right);
  if (op === '!=') return !looseEq(left, right);

  // <, > only meaningful for numbers / dates
  const ln = numericCoerce(left);
  const rn = numericCoerce(right);
  if (ln === undefined || rn === undefined) return false;
  if (op === '<') return ln < rn;
  if (op === '>') return ln > rn;
  return false;
}

function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'string') return String(a) === b;
  if (typeof a === 'string' && typeof b === 'number') return a === String(b);
  return false;
}

function numericCoerce(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string') {
    // ISO date prefix? Compare as ms-since-epoch.
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
      const t = Date.parse(v);
      return Number.isFinite(t) ? t : undefined;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function literalValue(lit: LiteralValue): unknown {
  switch (lit.kind) {
    case 'string':
      return lit.value;
    case 'number':
      return lit.value;
    case 'boolean':
      return lit.value;
    case 'null':
      return null;
  }
}

function stripHash(s: string): string {
  return s.startsWith('#') ? s.slice(1) : s;
}

// ============================================================================
// Field resolution + projection
// ============================================================================

function resolveFieldRaw(note: ParsedNote, field: string): unknown {
  // file.* metadata
  if (field === 'file.name') return note.name;
  if (field === 'file.path') return note.path;
  if (field === 'file.link') return note.path;
  if (field === 'file.tags') return note.tags;
  if (field === 'file.body') return note.body;
  if (field === 'file.mtime') return note.frontmatter?.['mtime'];
  if (field === 'file.ctime') return note.frontmatter?.['ctime'];

  // Plain frontmatter scalar.
  if (note.frontmatter && Object.prototype.hasOwnProperty.call(note.frontmatter, field)) {
    return note.frontmatter[field];
  }
  return undefined;
}

function projectField(note: ParsedNote, field: string): RunnerCell {
  if (field === 'file.link') {
    return { kind: 'link', path: note.path, label: note.name };
  }
  if (field === 'file.tags') {
    return { kind: 'tags', tags: [...note.tags] };
  }
  if (field === 'file.mtime' || field === 'file.ctime') {
    const raw = resolveFieldRaw(note, field);
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        return {
          kind: 'date',
          iso: raw,
          display: d.toLocaleDateString(),
        };
      }
    }
    return { kind: 'empty' };
  }
  const raw = resolveFieldRaw(note, field);
  if (raw === undefined || raw === null) return { kind: 'empty' };
  if (typeof raw === 'number') return { kind: 'number', value: raw };
  if (typeof raw === 'boolean') return { kind: 'boolean', value: raw };
  if (Array.isArray(raw)) {
    return { kind: 'text', value: raw.map((v) => String(v)).join(', ') };
  }
  if (typeof raw === 'string') return { kind: 'text', value: raw };
  return { kind: 'text', value: JSON.stringify(raw) };
}
